import { BrandLogo, Button, buttonClasses } from '@fruuit/ui'
import { Compass, LockKeyhole, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router'
import { signOut } from '../api/session'
import { useTitle } from '../lib/useTitle'

function StatusLayout({
  icon: Icon,
  title,
  children,
  actions,
}: {
  icon: LucideIcon
  title: string
  children: ReactNode
  actions: ReactNode
}) {
  return (
    <div className="flex min-h-dvh flex-col items-center bg-cream px-5 py-10">
      <header>
        <BrandLogo className="w-32" />
      </header>
      <main className="my-auto w-full max-w-lg py-12 text-center">
        <span
          aria-hidden="true"
          className="mx-auto flex size-14 items-center justify-center rounded-full bg-surface text-teal shadow-card ring-1 ring-line"
        >
          <Icon className="size-6" />
        </span>
        <h1 className="mt-6 font-display text-[28px] font-semibold leading-tight tracking-[-0.02em] text-ink">
          {title}
        </h1>
        <div className="mt-3 text-[15px] leading-relaxed text-sage">{children}</div>
        <div className="mt-8 flex flex-wrap justify-center gap-3">{actions}</div>
      </main>
      <footer className="text-xs text-sage">Fruuit Engage · Fruuit Consulting</footer>
    </div>
  )
}

/** Neutral no-access page: never says whether an account exists (P-ID-010). */
export function NoAccessPage() {
  useTitle('No access')
  const [params] = useSearchParams()
  const suspended = params.get('reason') === 'suspended'
  return (
    <StatusLayout
      icon={LockKeyhole}
      title={suspended ? 'Your organisation is suspended' : "This account doesn't have access"}
      actions={
        <Button variant="primary" onClick={() => void signOut()}>
          Sign in with another account
        </Button>
      }
    >
      {suspended
        ? 'Access to Fruuit Engage is paused for your organisation. Your Fruuit Consulting contact can tell you more.'
        : 'Fruuit Engage is invite-only. If you expected access, ask your Fruuit Consulting contact for an invitation, then sign in with the email address it was sent to.'}
    </StatusLayout>
  )
}

/** UI-043: unknown or unauthorised routes get the same neutral message. */
export function NotFoundPage() {
  useTitle('Not available')
  return (
    <StatusLayout
      icon={Compass}
      title="This page is not available"
      actions={
        <Link to="/" className={buttonClasses({ variant: 'primary' })}>
          Go to your workspace
        </Link>
      }
    >
      The address may be mistyped, or the page may not be available to your account.
    </StatusLayout>
  )
}
